import { useState, useEffect, useCallback, useRef } from "react";
import { Select, Switch, Slider, Form, Button } from "@timeedit/ui-components/antd";
import { useAppContext } from "../../contexts/AppContext";
import { useFlags } from "launchdarkly-react-client-sdk";
import API from "../../lib/TimeEditAPI";
import Language from "../../lib/Language";
import Log from "../../lib/Log";
import ReservationStatus from "../../lib/ReservationStatus";
import _ from "underscore";
import Typography from "@timeedit/ui-components/antd/typography/Typography";

const LISTABLE_STATUSES = [
    ReservationStatus.UNKNOWN,
    ReservationStatus.C_CONFIRMED,
    ReservationStatus.C_PRELIMINARY,
    ReservationStatus.C_PLANNED,
] as const;

const EMAIL_STATUS = {
    NONE: 0,
    ACCEPTED_AND_REJECTED: 30,
    REJECTED_ONLY: 34,
} as const;

const FIELDS_IN_SELECTION_LIST_STATUS = {
    OFF: 0,
    INLINE: 1,
    NEXT_TO: 2,
} as const;

const AccountContent = () => {
    const appContext = useAppContext();
    const flags = useFlags();
    const { user } = appContext;

    const [settings, setSettings] = useState<Record<string, any>>({});
    const [timezones, setTimezones] = useState<
        { shortName: string; name: string; offset: string }[]
    >([]);
    const [fontSizeChanged, setFontSizeChanged] = useState<boolean>(false);

    useEffect(() => {
        API.getTimezones((tz) => {
            setTimezones(tz);
        });
    }, [user?.language]);

    const getValue = (field: string) => {
        if (settings.hasOwnProperty(field)) {
            return settings[field];
        }
        return user?.[field];
    };

    const setValue = useCallback(
        (field: string, value: any) => {
            if (field === "showLists") {
                appContext.toggleSelection(undefined);
            }
            const obj = _.clone(settings);
            if (field === "fieldsInSelectionOptions") {
                if (value === FIELDS_IN_SELECTION_LIST_STATUS.OFF) {
                    obj.fieldsInSelection = false;
                    obj.fieldsNextToSelection = false;
                }
                if (value === FIELDS_IN_SELECTION_LIST_STATUS.INLINE) {
                    obj.fieldsInSelection = true;
                    obj.fieldsNextToSelection = false;
                }
                if (value === FIELDS_IN_SELECTION_LIST_STATUS.NEXT_TO) {
                    obj.fieldsInSelection = true;
                    obj.fieldsNextToSelection = true;
                }
            } else {
                obj[field] = value;
            }
            setSettings(obj);
        },
        [settings, appContext]
    );

    const saveDebounceRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        if (hasChanges()) {
            clearTimeout(saveDebounceRef.current);
            saveDebounceRef.current = setTimeout(() => {
                saveChanges();
            }, 200);
        }
    }, [settings]);

    const hasChanges = useCallback(() => {
        // eslint-disable-next-line no-restricted-syntax
        for (const prop in user) {
            if (
                user.hasOwnProperty(prop) &&
                settings[prop] !== undefined &&
                user[prop] !== settings[prop] &&
                prop !== "displayTimezones"
            ) {
                return true;
            }
        }
        if (fontSizeChanged) {
            return true;
        }
        return false;
    }, [user, settings]);

    const saveChanges = useCallback(() => {
        const obj: Record<string, any> = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const prop in user) {
            if (user.hasOwnProperty(prop)) {
                obj[prop] = settings.hasOwnProperty(prop) ? settings[prop] : user[prop];
            }
        }

        if (!user) {
            return;
        }
        const updatedUser = user.immutableSet(obj);

        updatedUser.save((success) => {
            if (!success) {
                Log.warning(Language.get("reset_password_could_not_send_message"));
                return;
            }
            appContext.update(user, updatedUser);
            setFontSizeChanged(false);
            setSettings({});
        });
    }, [user, settings, appContext]);

    const saveOtherTimezones = () => {
        if (!user) {
            return;
        }
        const updatedUser = user.immutableSet({ displayTimezones: settings.displayTimezones });
        updatedUser.save((success) => {
            if (!success) {
                Log.warning(Language.get("reset_password_could_not_send_message"));
                return;
            }
            document.location.reload();
        });
    };

    const handleFontSizeChange = useCallback(
        (value: number) => {
            if (user) {
                appContext.update(user, user.setFontSize(value));
            }
            setValue("fontSize", value);
            setFontSizeChanged(true);
        },
        [user, appContext, setValue]
    );

    const getFieldsInSelectionValue = () => {
        const fis = getValue("fieldsInSelection");
        const fnts = getValue("fieldsNextToSelection");
        if (fis && fnts) return FIELDS_IN_SELECTION_LIST_STATUS.NEXT_TO;
        if (fis) return FIELDS_IN_SELECTION_LIST_STATUS.INLINE;
        return FIELDS_IN_SELECTION_LIST_STATUS.OFF;
    };

    // Every hook above has run, so this is safe to return from: the panel shows
    // the signed-in user's settings and has nothing to show without one.
    if (!user) {
        return null;
    }

    const timezoneOptions = timezones.map((zone) => ({
        value: zone.shortName,
        label: zone.shortName,
    }));

    const haveTimezonesChanged = !_.isEqual(
        _.uniq(getValue("displayTimezones")).sort(),
        _.uniq(user.displayTimezones).sort()
    );

    const checkboxItems = [
        {
            field: "allowSingleClickReservation",
            label: "cal_res_side_user_single_click_res",
        },
        { field: "tooltipInSidebar", label: "nc_tooltip_in_sidebar" },
        { field: "jumpToNextType", label: "cal_res_side_user_jump_to_next_type" },
        { field: "showLists", label: "menu_view_lists" },
        { field: "mute", label: "nc_cal_res_side_user_mute" },
        { field: "enableMultiSelect", label: "nc_enable_multi_select" },
        {
            field: "showConfirmEntryRemovalWarning",
            label: "nc_show_confirm_entry_removal_warning",
        },
        { field: "showExtraInfo", label: "menu_view_additional_info" },
        { field: "promptForGroupName", label: "nc_prompt_for_group_name" },
    ].map((item) => (
        <label
            key={item.field}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                cursor: "pointer",
            }}
        >
            <Switch
                size="small"
                checked={Boolean(getValue(item.field))}
                onChange={(checked) => setValue(item.field, checked)}
            />
            {Language.get(item.label as any)}
        </label>
    ));

    const filterOtherTimezoneOptions = (savedTimezones, userTimezone) => {
        return _.uniq(
            savedTimezones.filter(
                (tzString) => tzString !== "" && tzString !== userTimezone.shortName
            )
        );
    };

    return (
        <div
            style={{
                paddingLeft: "24px",
                paddingRight: "24px",
                paddingTop: "16px",
                paddingBottom: "16px",
            }}
        >
            <Form
                labelAlign={"left"}
                labelWrap={true}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
            >
                <Form.Item
                    style={{ marginBottom: 0 }}
                    label={Language.get("nc_visible_timezones")}
                    tooltip={Language.get("nc_visible_timezones_help")}
                >
                    <Select
                        size="small"
                        mode="multiple"
                        style={{ marginBottom: "4px" }}
                        value={filterOtherTimezoneOptions(
                            getValue("displayTimezones"),
                            user.actualTimezone
                        )}
                        onChange={(val) => setValue("displayTimezones", val)}
                        options={timezoneOptions.map((tz) => ({
                            ...tz,
                        }))}
                        showSearch
                        optionFilterProp="label"
                    />
                </Form.Item>
                <Typography
                    style={{ textAlign: "right", marginBottom: "8px" }}
                    className="ant-form-item-explain"
                >
                    {Language.get("nc_page_reload_required")}
                    <Button
                        style={{ marginLeft: "8px" }}
                        size="small"
                        type="primary"
                        title={Language.get("nc_apply_changes")}
                        onClick={saveOtherTimezones}
                        disabled={!haveTimezonesChanged}
                    >
                        {Language.get("nc_apply")}
                    </Button>
                </Typography>

                <Form.Item
                    style={{ marginBottom: 0 }}
                    label={Language.get("nc_cal_res_side_user_request_email")}
                >
                    <Select
                        size="small"
                        value={getValue("emailStatus")}
                        onChange={(val) => setValue("emailStatus", val)}
                        options={[
                            {
                                value: EMAIL_STATUS.ACCEPTED_AND_REJECTED,
                                label: Language.get(
                                    "cal_res_side_user_email_accepted_and_rejected"
                                ),
                            },
                            {
                                value: EMAIL_STATUS.REJECTED_ONLY,
                                label: Language.get("cal_res_side_user_email_rejected_only"),
                            },
                            {
                                value: EMAIL_STATUS.NONE,
                                label: Language.get("cal_res_side_user_email_none"),
                            },
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    style={{ marginBottom: 0 }}
                    label={Language.get("cal_res_side_user_reservation_status_title")}
                >
                    <Select
                        size="small"
                        value={getValue("reservationStatus")}
                        onChange={(val) => setValue("reservationStatus", val)}
                        options={LISTABLE_STATUSES.map((status) => ({
                            value: status.id,
                            label: status.getStatusName(),
                        }))}
                    />
                </Form.Item>
                <Form.Item
                    style={{ marginBottom: 0 }}
                    label={Language.get("nc_fields_in_selection_list_title")}
                >
                    <Select
                        size="small"
                        value={getFieldsInSelectionValue()}
                        onChange={(val) => setValue("fieldsInSelectionOptions", val)}
                        options={[
                            {
                                value: FIELDS_IN_SELECTION_LIST_STATUS.OFF,
                                label: Language.get("nc_fields_in_selection_list_off"),
                            },
                            {
                                value: FIELDS_IN_SELECTION_LIST_STATUS.INLINE,
                                label: Language.get("nc_fields_in_selection_list"),
                            },
                            {
                                value: FIELDS_IN_SELECTION_LIST_STATUS.NEXT_TO,
                                label: Language.get("nc_fields_next_to_selection_list"),
                            },
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    style={{ marginBottom: 0 }}
                    label={Language.get("nc_reservation_text_size")}
                >
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <div style={{ paddingRight: "8px" }}>9</div>
                        <Slider
                            style={{ flexGrow: "1" }}
                            min={9}
                            max={15}
                            value={getValue("fontSize")}
                            marks={{ 10: `10 (${Language.get("nc_theme_default")})` }}
                            onChange={handleFontSizeChange}
                        />
                        <div style={{ paddingLeft: "8px" }}>15</div>
                    </div>
                </Form.Item>
            </Form>

            {/* Boolean toggles */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px 16px",
                    marginTop: 12,
                }}
            >
                {checkboxItems}
                {appContext.prefsComponentActive && (
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                            cursor: "pointer",
                        }}
                    >
                        <Switch
                            size="small"
                            checked={Boolean(getValue("createGroupsForReservationTracks"))}
                            onChange={(checked) =>
                                setValue("createGroupsForReservationTracks", checked)
                            }
                        />
                        {Language.get("nc_create_groups_for_reservation_tracks")}
                    </label>
                )}
                {flags.coreShowCalendarTimeIndicator && (
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                            cursor: "pointer",
                        }}
                    >
                        <Switch
                            size="small"
                            checked={Boolean(getValue("showRoundedTimeIndicator"))}
                            onChange={(checked) => setValue("showRoundedTimeIndicator", checked)}
                        />
                        {Language.get("nc_account_indicate_start_time_in_calendar")}
                    </label>
                )}
                {flags.coreShowSecondarySizeOption && (
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                            cursor: "pointer",
                        }}
                    >
                        <Switch
                            size="small"
                            checked={Boolean(getValue("useSecondarySize"))}
                            onChange={(checked) => setValue("useSecondarySize", checked)}
                        />
                        {Language.get("nc_account_use_secondary_size")}
                    </label>
                )}
                {appContext.showEnableCustomWeekNames && (
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                            cursor: "pointer",
                        }}
                    >
                        <Switch
                            size="small"
                            checked={Boolean(getValue("useCustomWeekNames"))}
                            onChange={(checked) => setValue("useCustomWeekNames", checked)}
                        />
                        {Language.get("nc_use_custom_week_names")}
                    </label>
                )}
            </div>
        </div>
    );
};

export default AccountContent;
